import { useState, useRef, useEffect } from 'react';
import { OpenRouterModel } from '../../services/api/openRouter';
import { Bot, ChevronDown, Sparkles, Search, X } from 'lucide-react';

interface Props {
    selectedModel: string;
    onSelect: (modelId: string) => void;
    disabled?: boolean;
    models: OpenRouterModel[];
}

export default function ModelSelector({ selectedModel, onSelect, disabled, models }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedModelData = models.find(m => m.id === selectedModel);
    const displayName = selectedModelData?.name || selectedModel || 'Selecione um modelo...';

    const filteredModels = models.filter(model =>
        model.name.toLowerCase().includes(search.toLowerCase()) ||
        model.id.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (modelId: string) => {
        onSelect(modelId);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-navy-200 dark:border-navy-700 p-4 shadow-sm transition-all duration-200 hover:shadow-md hover:border-accent-200 dark:hover:border-accent-800 group">
            <div className="flex items-center space-x-2 mb-3">
                <div className="p-1.5 bg-accent-100 dark:bg-accent-900/30 rounded-lg group-hover:bg-accent-200 dark:group-hover:bg-accent-900/50 transition-colors">
                    <Sparkles className="h-4 w-4 text-accent-600 dark:text-accent-400" />
                </div>
                <label className="block text-xs font-bold text-navy-600 dark:text-navy-300 uppercase tracking-wider">
                    Modelo de IA
                </label>
            </div>

            <div className="relative" ref={containerRef}>
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    className="appearance-none flex items-center w-full pl-10 pr-10 py-3 text-sm font-medium border border-navy-200 dark:border-navy-600 rounded-lg bg-navy-50/50 dark:bg-navy-900 text-navy-900 dark:text-navy-100 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 focus:bg-white dark:focus:bg-navy-800 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer hover:border-navy-300 dark:hover:border-navy-500 text-left"
                >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Bot className="h-5 w-5 text-navy-400 group-hover:text-accent-500 transition-colors" />
                    </div>
                    <span className="truncate flex-1">{displayName}</span>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-navy-400">
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-navy-800 border border-navy-200 dark:border-navy-600 rounded-lg shadow-lg overflow-hidden">
                        <div className="p-2 border-b border-navy-200 dark:border-navy-700">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy-400" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar modelo..."
                                    className="w-full pl-9 pr-8 py-2 text-sm border border-navy-200 dark:border-navy-600 rounded-md bg-navy-50 dark:bg-navy-900 text-navy-900 dark:text-navy-100 placeholder-navy-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400"
                                />
                                {search && (
                                    <button
                                        onClick={() => setSearch('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-navy-100 dark:hover:bg-navy-700 rounded"
                                    >
                                        <X className="h-3 w-3 text-navy-400" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="max-h-60 overflow-y-auto">
                            {filteredModels.length > 0 ? (
                                filteredModels.map((model) => (
                                    <button
                                        key={model.id}
                                        onClick={() => handleSelect(model.id)}
                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors flex items-center justify-between ${
                                            model.id === selectedModel
                                                ? 'bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300'
                                                : 'text-navy-700 dark:text-navy-200'
                                        }`}
                                    >
                                        <span className="truncate">{model.name}</span>
                                        {model.id === selectedModel && (
                                            <span className="ml-2 text-accent-500">✓</span>
                                        )}
                                    </button>
                                ))
                            ) : (
                                <div className="px-4 py-3 text-sm text-navy-500 dark:text-navy-400 text-center">
                                    Nenhum modelo encontrado
                                </div>
                            )}
                        </div>

                        <div className="px-4 py-3 border-t border-navy-200 dark:border-navy-700 bg-gradient-to-r from-accent-500/10 via-transparent to-accent-500/10 flex items-center justify-center">
                            <div className="flex items-center space-x-2 text-accent-600 dark:text-accent-400 bg-accent-100 dark:bg-accent-900/30 px-3 py-1 rounded-full border border-accent-200 dark:border-accent-800 shadow-sm">
                                <Sparkles className="h-4 w-4 animate-pulse" />
                                <span className="text-sm font-extrabold tracking-wide">
                                    {models.length} modelos disponíveis
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <p className="text-[10px] text-navy-400 dark:text-navy-500 mt-2 px-1">
                Digite para buscar entre os modelos disponíveis.
            </p>
        </div>
    );
}
